import React, { useState, useEffect, useCallback, useRef } from 'react';
import Plotly from 'plotly.js/dist/plotly';
import { useDashboard } from '../../context/DashboardContext';
import { useConfiguration } from '../../context/ConfigurationContext';
import { impactsData } from '../../data/impactsData';
import { getIndicatorStatus, fetchIndicatorStatus, clearIndicatorStatusCache } from '../../utils/priorityUtils';
import IndicatorTabs from '../detail/IndicatorTabs';
import PowerOutageModeSelector from '../detail/PowerOutageModeSelector';
import GroundwaterModeSelector from '../detail/GroundwaterModeSelector';
import InformalWorkersModeSelector from '../detail/InformalWorkersModeSelector';
import ConstructionLaborModeSelector from '../detail/ConstructionLaborModeSelector';
import TransportDisruptionsModeSelector from '../detail/TransportDisruptionsModeSelector';
import RoadClosuresModeSelector from '../detail/RoadClosuresModeSelector';
import TrafficSignalFailuresModeSelector from '../detail/TrafficSignalFailuresModeSelector';
import CoolingSystemFailuresModeSelector from '../detail/CoolingSystemFailuresModeSelector';
import SchoolClosuresModeSelector from '../detail/SchoolClosuresModeSelector';
import SchoolCoolingModeSelector from '../detail/SchoolCoolingModeSelector';
import WaterSupplyModeSelector from '../detail/WaterSupplyModeSelector';
import WaterTankersModeSelector from '../detail/WaterTankersModeSelector';
import HeatDifferentialModeSelector from '../detail/HeatDifferentialModeSelector';
import SurfaceTemperatureModeSelector from '../detail/SurfaceTemperatureModeSelector';
import ShelterOccupancyModeSelector from '../detail/ShelterOccupancyModeSelector';
import RecommendedMeasures from '../detail/RecommendedMeasures';
import MapContainer from '../detail/MapContainer';
import TrendContainer from '../detail/TrendContainer';
import ForecastTableContainer from '../detail/ForecastTableContainer';
import DateRangeSelector from '../detail/DateRangeSelector';

const DetailView = () => {
  const {
    currentImpact,
    currentCity,
    currentIndicator,
    setCurrentIndicator,
    selectedWard,
    setSelectedWard,
    currentPowerOutageMode,
    setCurrentPowerOutageMode,
    currentGroundwaterMode,
    setCurrentGroundwaterMode,
    currentInformalWorkersMode,
    setCurrentInformalWorkersMode,
    currentConstructionLaborMode,
    setCurrentConstructionLaborMode,
    currentTransportDisruptionsMode,
    setCurrentTransportDisruptionsMode,
    currentRoadClosuresMode,
    setCurrentRoadClosuresMode,
    currentTrafficSignalFailuresMode,
    setCurrentTrafficSignalFailuresMode,
    currentCoolingSystemFailuresMode,
    setCurrentCoolingSystemFailuresMode,
    currentSchoolClosuresMode,
    setCurrentSchoolClosuresMode,
    currentSchoolCoolingMode,
    setCurrentSchoolCoolingMode,
    currentWaterSupplyMode,
    setCurrentWaterSupplyMode,
    currentWaterTankersMode,
    setCurrentWaterTankersMode,
    currentHeatDifferentialMode,
    setCurrentHeatDifferentialMode,
    currentSurfaceTemperatureMode,
    setCurrentSurfaceTemperatureMode,
    currentShelterOccupancyMode,
    setCurrentShelterOccupancyMode,
    forecastDay,
    setForecastDay,
    hasIndicatorData,
    targetIndicator,
    setTargetIndicator,
    currentGeoJSON
  } = useDashboard();

  const [currentSector, setCurrentSector] = useState('');
  const [currentThreshold, setCurrentThreshold] = useState('');
  const [currentDataNeeded, setCurrentDataNeeded] = useState('');
  const [showTrendContainer, setShowTrendContainer] = useState(true);
  const [isChartCollapsed, setIsChartCollapsed] = useState(false);
  const [forecastDaysAvailable, setForecastDaysAvailable] = useState(6);  // Default to 6 days
  const [startDate, setStartDate] = useState(new Date(2025, 4, 1)); // May 1, 2025
  const [endDate, setEndDate] = useState(new Date(2025, 4, 31)); // May 31, 2025
  const [forecastViewMode, setForecastViewMode] = useState('split'); // 'split' (both) or 'mapOnly' (full-screen map) for Forecast mode
  const initialLoadRef = useRef(true);
  const visualizationContainerRef = useRef(null);

  // Generate available months from April 2021 to current date
  const generateAvailableMonths = () => {
    const months = [];
    const startYear = 2021;
    const startMonth = 3; // April (0-indexed)
    const currentDate = new Date();
    const currentYear = currentDate.getFullYear();
    const currentMonth = currentDate.getMonth();

    for (let year = startYear; year <= currentYear; year++) {
      const monthStart = (year === startYear) ? startMonth : 0;
      const monthEnd = (year === currentYear) ? currentMonth : 11;
      
      for (let month = monthStart; month <= monthEnd; month++) {
        months.push(new Date(year, month, 1));
      }
    }
    return months;
  };

  const availableMonths = generateAvailableMonths();

  const [statusUpdateCounter, setStatusUpdateCounter] = React.useState(0);

  // Listen for configuration changes
  React.useEffect(() => {
    const handleConfigurationChange = () => {
      // Force re-render when configuration changes
      setStatusUpdateCounter(prev => prev + 1);
    };
    
    window.addEventListener('configurationChanged', handleConfigurationChange);
    return () => window.removeEventListener('configurationChanged', handleConfigurationChange);
  }, []);

  // Fetch indicator statuses in background (non-blocking)
  React.useEffect(() => {
    if (!currentCity || !currentImpact) {
      return;
    }
    
    // Don't block UI - fetch statuses in background and update as they complete
    currentImpact.sectors.forEach(sector => {
      sector.indicators.forEach(ind => {
        if (hasIndicatorData(currentCity, ind.name)) {
          // Fetch each status independently without blocking
          fetchIndicatorStatus(ind.name, currentCity)
            .then(() => {
              // Trigger re-render when status is loaded
              setStatusUpdateCounter(prev => prev + 1);
            })
            .catch(err => {
              console.warn(`Failed to fetch status for ${ind.name}:`, err);
              // Don't block UI on errors
            });
        }
      });
    });
  }, [currentCity, currentImpact, hasIndicatorData]);

  // Clear cache and refetch when thresholds change so indicator status (Normal/Warning/High) matches new threshold
  React.useEffect(() => {
    const handleThresholdChange = async () => {
      if (!currentCity || !currentImpact) return;
      clearIndicatorStatusCache();
      const promises = [];
      currentImpact.sectors.forEach(sector => {
        sector.indicators.forEach(ind => {
          if (hasIndicatorData(currentCity, ind.name)) {
            promises.push(
              fetchIndicatorStatus(ind.name, currentCity).catch(err => {
                console.warn(`Failed to refetch status for ${ind.name}:`, err);
              })
            );
          }
        });
      });
      await Promise.all(promises);
      setStatusUpdateCounter(prev => prev + 1);
    };

    window.addEventListener('thresholdChanged', handleThresholdChange);
    return () => window.removeEventListener('thresholdChanged', handleThresholdChange);
  }, [currentCity, currentImpact]);

  // Get all indicators from all sectors - Show all indicators, with lock icon for those without data
  const allIndicators = React.useMemo(() => {
    if (!currentImpact) return { indicators: [], firstIndicatorWithData: null, firstIndicatorSector: null };
    
    const indicators = [];
    let firstIndicatorWithData = null;
    let firstIndicatorSector = null;

    currentImpact.sectors.forEach((sector) => {
      sector.indicators.forEach((ind) => {
        const hasData = hasIndicatorData(currentCity, ind.name);
        // Include all indicators, regardless of data availability
        const status = hasData ? getIndicatorStatus(ind.name, currentCity) : null;
        indicators.push({
          indicator: ind,
          sector: sector.name,
          hasData: hasData, // Set based on actual data availability
          status: status
        });
        
        // Track first indicator with data for initial selection
        if (!firstIndicatorWithData && hasData) {
          firstIndicatorWithData = ind;
          firstIndicatorSector = sector.name;
        }
      });
    });

    return { indicators, firstIndicatorWithData, firstIndicatorSector };
  }, [currentImpact, currentCity, statusUpdateCounter, hasIndicatorData]); // Include hasIndicatorData to react to configuration changes

  // Determine if an indicator should show trend data
  const shouldShowTrend = useCallback((indicatorName) => {
    const isPowerOutage = indicatorName === 'Power outages (duration and geographic scope)' || indicatorName === 'Power Outage';
    const isGroundwater = indicatorName === 'Groundwater stress levels';
    const isInformalWorkers =
      indicatorName === 'No show rate among informal workers' ||
      indicatorName === 'Daily wage loss (street vendors, rickshaw pullers)';
    const isConstructionLabor =
      indicatorName === 'Lost labor hours due to heat stress' ||
      indicatorName === '% of workers without access to shade/water' ||
      indicatorName === 'Reported workplace heat illness cases';
    return isPowerOutage || isGroundwater || isInformalWorkers || isConstructionLabor;
  }, []);

  // Handle indicator selection
  const handleIndicatorSelect = useCallback((indicatorName, sector, threshold, dataNeeded, hasData) => {
    if (!hasData) return;
    
    setCurrentIndicator(indicatorName);
    setCurrentSector(sector);
    setCurrentThreshold(threshold);
    setCurrentDataNeeded(dataNeeded);
    setSelectedWard(null);

    const isPowerOutage = indicatorName === 'Power outages (duration and geographic scope)' || indicatorName === 'Power Outage';
    const isGroundwater = indicatorName === 'Groundwater stress levels';
    const isInformalWorkers =
      indicatorName === 'No show rate among informal workers' ||
      indicatorName === 'Daily wage loss (street vendors, rickshaw pullers)';
    const isConstructionLabor =
      indicatorName === 'Lost labor hours due to heat stress' ||
      indicatorName === '% of workers without access to shade/water' ||
      indicatorName === 'Reported workplace heat illness cases';
    const isTransportDisruptions = indicatorName === 'Public transport disruptions (rail, metro, bus)';
    const isRoadClosures = indicatorName === 'Road/bridge degradation or closures';
    const isTrafficSignalFailures = indicatorName === 'Traffic signal / equipment failures';
    const isCoolingSystemFailures = indicatorName === 'Cooling system failure in public buildings';
    const isSchoolClosures = indicatorName === 'No. of school/college closure days due to heat';
    const isSchoolCooling = indicatorName === '% of schools without basic cooling infrastructure';
    const isWaterSupply = indicatorName === 'Drop in per capita water availability (lpcd)';
    const isWaterTankers = indicatorName === 'Percentage of wards dependent on water tankers';
    const isHeatDifferential = indicatorName === 'Temperature differential (urban vs. rural)';
    const isSurfaceTemperature = indicatorName === 'Surface temperature & low vegetation zones';
    const isShelterOccupancy = indicatorName === 'Homeless and street dwellers exposed';
    
    if (isPowerOutage) {
      setCurrentPowerOutageMode('severity');
      setShowTrendContainer(true);
    } else if (isGroundwater) {
      setCurrentGroundwaterMode('severity');
      setShowTrendContainer(true);
    } else if (isInformalWorkers) {
      setCurrentInformalWorkersMode('severity');
      setShowTrendContainer(true);
    } else if (isConstructionLabor) {
      setCurrentConstructionLaborMode('severity');
      setShowTrendContainer(true);
    } else if (isTransportDisruptions) {
      setCurrentTransportDisruptionsMode('severity');
      setShowTrendContainer(true);
    } else if (isRoadClosures) {
      setCurrentRoadClosuresMode('severity');
      setShowTrendContainer(true);
    } else if (isTrafficSignalFailures) {
      setCurrentTrafficSignalFailuresMode('severity');
      setShowTrendContainer(true);
    } else if (isCoolingSystemFailures) {
      setCurrentCoolingSystemFailuresMode('severity');
      setShowTrendContainer(true);
    } else if (isSchoolClosures) {
      setCurrentSchoolClosuresMode('severity');
      setShowTrendContainer(true);
    } else if (isSchoolCooling) {
      setCurrentSchoolCoolingMode('severity');
      setShowTrendContainer(true);
    } else if (isWaterSupply) {
      setCurrentWaterSupplyMode('severity');
      setShowTrendContainer(true);
    } else if (isWaterTankers) {
      setCurrentWaterTankersMode('severity');
      setShowTrendContainer(true);
    } else if (isHeatDifferential) {
      setCurrentHeatDifferentialMode('severity');
      setShowTrendContainer(true);
    } else if (isSurfaceTemperature) {
      setCurrentSurfaceTemperatureMode('severity');
      setShowTrendContainer(true);
    } else if (isShelterOccupancy) {
      setCurrentShelterOccupancyMode('severity');
      setShowTrendContainer(true);
    } else {
      setShowTrendContainer(shouldShowTrend(indicatorName));
    }
  }, [setCurrentIndicator, setSelectedWard, setCurrentPowerOutageMode, setCurrentGroundwaterMode, setCurrentInformalWorkersMode, setCurrentConstructionLaborMode, setCurrentTransportDisruptionsMode, setCurrentRoadClosuresMode, setCurrentTrafficSignalFailuresMode, setCurrentCoolingSystemFailuresMode, setCurrentSchoolClosuresMode, setCurrentSchoolCoolingMode, setCurrentWaterSupplyMode, setCurrentWaterTankersMode, setCurrentHeatDifferentialMode, setCurrentSurfaceTemperatureMode, setCurrentShelterOccupancyMode, shouldShowTrend]);

  const handlePowerOutageModeChange = useCallback((mode) => {
    setCurrentPowerOutageMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentPowerOutageMode]);

  const handleGroundwaterModeChange = useCallback((mode) => {
    setCurrentGroundwaterMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentGroundwaterMode]);

  const handleInformalWorkersModeChange = useCallback((mode) => {
    setCurrentInformalWorkersMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentInformalWorkersMode]);

  const handleConstructionLaborModeChange = useCallback((mode) => {
    setCurrentConstructionLaborMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentConstructionLaborMode]);

  const handleTransportDisruptionsModeChange = useCallback((mode) => {
    setCurrentTransportDisruptionsMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentTransportDisruptionsMode]);

  const handleRoadClosuresModeChange = useCallback((mode) => {
    setCurrentRoadClosuresMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentRoadClosuresMode]);

  const handleTrafficSignalFailuresModeChange = useCallback((mode) => {
    setCurrentTrafficSignalFailuresMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentTrafficSignalFailuresMode]);

  const handleCoolingSystemFailuresModeChange = useCallback((mode) => {
    setCurrentCoolingSystemFailuresMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentCoolingSystemFailuresMode]);

  const handleSchoolClosuresModeChange = useCallback((mode) => {
    setCurrentSchoolClosuresMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentSchoolClosuresMode]);

  const handleSchoolCoolingModeChange = useCallback((mode) => {
    setCurrentSchoolCoolingMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentSchoolCoolingMode]);

  const handleWaterSupplyModeChange = useCallback((mode) => {
    setCurrentWaterSupplyMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentWaterSupplyMode]);

  const handleWaterTankersModeChange = useCallback((mode) => {
    setCurrentWaterTankersMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentWaterTankersMode]);

  const handleHeatDifferentialModeChange = useCallback((mode) => {
    setCurrentHeatDifferentialMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentHeatDifferentialMode]);

  const handleSurfaceTemperatureModeChange = useCallback((mode) => {
    setCurrentSurfaceTemperatureMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentSurfaceTemperatureMode]);

  const handleShelterOccupancyModeChange = useCallback((mode) => {
    setCurrentShelterOccupancyMode(mode);
    setShowTrendContainer(true);
    setIsChartCollapsed(false);
  }, [setCurrentShelterOccupancyMode]);

  // Handle target indicator when it's set (e.g., from clicking on indicator in summary view)
  useEffect(() => {
    if (!currentImpact || !targetIndicator || allIndicators.indicators.length === 0) return;

    const { indicators } = allIndicators;
    const targetItem = indicators.find(
      item => item.indicator.name === targetIndicator.name && item.sector === targetIndicator.sector
    );
    
    // Only select if the target indicator is different from current
    if (targetItem && targetItem.hasData && currentIndicator !== targetItem.indicator.name) {
      handleIndicatorSelect(
        targetItem.indicator.name,
        targetItem.sector,
        targetItem.indicator.threshold,
        targetItem.indicator.dataNeeded,
        true
      );
      setTargetIndicator(null);
      return;
    } else if (targetItem && currentIndicator === targetItem.indicator.name) {
      // Already selected, just clear the target
      setTargetIndicator(null);
    }
  }, [targetIndicator, allIndicators, currentImpact, currentIndicator, handleIndicatorSelect, setTargetIndicator]);

  // Initial load - select first indicator with data (only if no target indicator and no current indicator)
  useEffect(() => {
    if (!currentImpact || !initialLoadRef.current) return;

    const { firstIndicatorWithData, firstIndicatorSector } = allIndicators;
    
    // Only select first indicator if there's no target indicator and no current indicator set
    if (!targetIndicator && !currentIndicator && firstIndicatorWithData && firstIndicatorSector) {
      handleIndicatorSelect(
        firstIndicatorWithData.name,
        firstIndicatorSector,
        firstIndicatorWithData.threshold,
        firstIndicatorWithData.dataNeeded,
        true
      );
    }
    
    initialLoadRef.current = false;
  }, [currentImpact, allIndicators, targetIndicator, currentIndicator, handleIndicatorSelect]);

  // Reset initial load ref when impact changes
  useEffect(() => {
    initialLoadRef.current = true;
  }, [currentImpact?.id]);

  // Reset DetailView state when navigating away (component will unmount, but this ensures clean state)
  useEffect(() => {
    // Reset local state when component mounts or when currentView changes
    if (!currentImpact || !currentCity) {
      setCurrentSector('');
      setCurrentThreshold('');
      setCurrentDataNeeded('');
      setShowTrendContainer(true);
      setIsChartCollapsed(false);
      setForecastViewMode('split'); // Reset to split view (map and table)
      setStartDate(new Date(2025, 4, 1)); // May 1, 2025
      setEndDate(new Date(2025, 4, 31)); // May 31, 2025
    }
  }, [currentImpact, currentCity]);

  // Resize map when chart collapse state changes
  useEffect(() => {
    // Function to resize the map - try multiple selectors
    const resizeMap = () => {
      // Try different selectors to find the Plotly plot
      const selectors = [
        '.map-container .js-plotly-plot',
        '.map-container .plotly',
        '.map-container div[class*="plotly"]',
        '#plotly-map-div .js-plotly-plot'
      ];
      
      for (const selector of selectors) {
        const mapDiv = document.querySelector(selector);
        if (mapDiv) {
          try {
            Plotly.Plots.resize(mapDiv);
            break; // Successfully resized, exit loop
          } catch (e) {
            // Silently continue to next selector
          }
        }
      }
    };

    // Immediate synchronous resize attempt
    resizeMap();

    // Immediate resize in next frame (double RAF for better timing)
    requestAnimationFrame(() => {
      resizeMap();
      requestAnimationFrame(() => {
        resizeMap();
      });
    });

    // Multiple resize attempts at different intervals
    const timers = [
      setTimeout(() => resizeMap(), 10),
      setTimeout(() => resizeMap(), 25),
      setTimeout(() => resizeMap(), 50),
      setTimeout(() => resizeMap(), 100),
      setTimeout(() => {
        resizeMap();
        setTimeout(() => resizeMap(), 50);
      }, 350)
    ];

    // Use ResizeObserver to watch for container size changes
    const container = visualizationContainerRef.current || document.querySelector('.visualization-container');
    let resizeObserver = null;
    
    if (container && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        resizeMap();
      });
      resizeObserver.observe(container);
    }

    // Trigger window resize event to help Plotly detect the change
    const triggerWindowResize = () => {
      window.dispatchEvent(new Event('resize'));
    };
    
    // Trigger window resize at multiple intervals
    triggerWindowResize();
    const resizeTimers = [
      setTimeout(triggerWindowResize, 10),
      setTimeout(triggerWindowResize, 50),
      setTimeout(triggerWindowResize, 350)
    ];

    return () => {
      timers.forEach(timer => clearTimeout(timer));
      resizeTimers.forEach(timer => clearTimeout(timer));
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [isChartCollapsed, currentPowerOutageMode, currentGroundwaterMode, currentTransportDisruptionsMode, currentRoadClosuresMode, currentTrafficSignalFailuresMode, currentCoolingSystemFailuresMode, currentSchoolClosuresMode, currentSchoolCoolingMode, currentWaterSupplyMode, currentWaterTankersMode, currentHeatDifferentialMode, currentSurfaceTemperatureMode, currentShelterOccupancyMode]);

  if (!currentImpact) {
    return <div>Loading...</div>;
  }

  const isPowerOutageIndicator = currentIndicator === 'Power outages (duration and geographic scope)' || currentIndicator === 'Power Outage';
  const isGroundwaterIndicator = currentIndicator === 'Groundwater stress levels';
  const isInformalWorkersIndicator =
    currentIndicator === 'No show rate among informal workers' ||
    currentIndicator === 'Daily wage loss (street vendors, rickshaw pullers)';
  const isConstructionLaborIndicator =
    currentIndicator === 'Lost labor hours due to heat stress' ||
    currentIndicator === '% of workers without access to shade/water' ||
    currentIndicator === 'Reported workplace heat illness cases';
  const isTransportDisruptionsIndicator = currentIndicator === 'Public transport disruptions (rail, metro, bus)';
  const isRoadClosuresIndicator = currentIndicator === 'Road/bridge degradation or closures';
  const isTrafficSignalFailuresIndicator = currentIndicator === 'Traffic signal / equipment failures';
  const isCoolingSystemFailuresIndicator = currentIndicator === 'Cooling system failure in public buildings';
  const isSchoolClosuresIndicator = currentIndicator === 'No. of school/college closure days due to heat';
  const isSchoolCoolingIndicator = currentIndicator === '% of schools without basic cooling infrastructure';
  const isWaterSupplyIndicator = currentIndicator === 'Drop in per capita water availability (lpcd)';
  const isWaterTankersIndicator = currentIndicator === 'Percentage of wards dependent on water tankers';
  const isHeatDifferentialIndicator = currentIndicator === 'Temperature differential (urban vs. rural)';
  const isSurfaceTemperatureIndicator = currentIndicator === 'Surface temperature & low vegetation zones';
  const isShelterOccupancyIndicator = currentIndicator === 'Homeless and street dwellers exposed';

  // Map View toggle for same row as "Visualization Mode" (non-forecast)
  const mapViewToggleHeaderAction = showTrendContainer ? (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
      <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Map View</span>
      <button
        onClick={() => {
          setIsChartCollapsed(!isChartCollapsed);
          setTimeout(() => {
            const mapDiv = document.querySelector('.map-container .js-plotly-plot');
            if (mapDiv) Plotly.Plots.resize(mapDiv);
            window.dispatchEvent(new Event('resize'));
          }, 0);
        }}
        style={{
          position: 'relative', width: '48px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
          background: isChartCollapsed ? '#27ae60' : '#ccc', transition: 'background 0.3s ease', outline: 'none', padding: 0
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        title={isChartCollapsed ? 'Show map and chart' : 'Show map only'}
      >
        <span style={{
          position: 'absolute', top: '2px', left: isChartCollapsed ? '26px' : '2px', width: '20px', height: '20px',
          borderRadius: '50%', background: 'white', transition: 'left 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>
  ) : null;

  // Map View toggle for Forecast mode (same row as "Visualization Mode")
  const forecastMapViewToggleHeaderAction = (
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
      <span style={{ fontSize: '0.85rem', color: '#666', fontWeight: 500 }}>Map View</span>
      <button
        onClick={() => {
          setForecastViewMode(forecastViewMode === 'split' ? 'mapOnly' : 'split');
          if (forecastViewMode === 'split') {
            setTimeout(() => {
              const mapDiv = document.querySelector('.map-container .js-plotly-plot');
              if (mapDiv) Plotly.Plots.resize(mapDiv);
              window.dispatchEvent(new Event('resize'));
            }, 0);
          }
        }}
        style={{
          position: 'relative', width: '48px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer',
          background: forecastViewMode === 'mapOnly' ? '#27ae60' : '#ccc', transition: 'background 0.3s ease', outline: 'none', padding: 0
        }}
        onMouseEnter={(e) => { e.currentTarget.style.opacity = '0.9'; }}
        onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
        title={forecastViewMode === 'split' ? 'Switch to full-screen map view' : 'Switch to split view (map and table)'}
      >
        <span style={{
          position: 'absolute', top: '2px', left: forecastViewMode === 'mapOnly' ? '26px' : '2px', width: '20px', height: '20px',
          borderRadius: '50%', background: 'white', transition: 'left 0.3s ease', boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }} />
      </button>
    </div>
  );

  return (
    <section id="view-detail">
      <div className="detail-layout">
        <IndicatorTabs
          indicators={allIndicators.indicators}
          currentIndicator={currentIndicator}
          onSelect={handleIndicatorSelect}
        />

        {isPowerOutageIndicator && (
          <PowerOutageModeSelector
            currentMode={currentPowerOutageMode}
            onModeChange={handlePowerOutageModeChange}
            indicatorName={currentIndicator}
            headerAction={currentPowerOutageMode === 'forecast' ? forecastMapViewToggleHeaderAction : mapViewToggleHeaderAction}
          />
        )}
        {isGroundwaterIndicator && (
          <GroundwaterModeSelector
            currentMode={currentGroundwaterMode}
            onModeChange={handleGroundwaterModeChange}
            indicatorName={currentIndicator}
            headerAction={currentGroundwaterMode === 'forecast' ? forecastMapViewToggleHeaderAction : mapViewToggleHeaderAction}
          />
        )}
        {isInformalWorkersIndicator && (
          <InformalWorkersModeSelector
            currentMode={currentInformalWorkersMode}
            onModeChange={handleInformalWorkersModeChange}
            indicatorName={currentIndicator}
            headerAction={currentInformalWorkersMode === 'forecast' ? forecastMapViewToggleHeaderAction : mapViewToggleHeaderAction}
          />
        )}
        {isConstructionLaborIndicator && (
          <ConstructionLaborModeSelector
            currentMode={currentConstructionLaborMode}
            onModeChange={handleConstructionLaborModeChange}
            indicatorName={currentIndicator}
            headerAction={currentConstructionLaborMode === 'forecast' ? forecastMapViewToggleHeaderAction : mapViewToggleHeaderAction}
          />
        )}
        {isTransportDisruptionsIndicator && (
          <TransportDisruptionsModeSelector
            currentMode={currentTransportDisruptionsMode}
            onModeChange={handleTransportDisruptionsModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isRoadClosuresIndicator && (
          <RoadClosuresModeSelector
            currentMode={currentRoadClosuresMode}
            onModeChange={handleRoadClosuresModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isTrafficSignalFailuresIndicator && (
          <TrafficSignalFailuresModeSelector
            currentMode={currentTrafficSignalFailuresMode}
            onModeChange={handleTrafficSignalFailuresModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isCoolingSystemFailuresIndicator && (
          <CoolingSystemFailuresModeSelector
            currentMode={currentCoolingSystemFailuresMode}
            onModeChange={handleCoolingSystemFailuresModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isSchoolClosuresIndicator && (
          <SchoolClosuresModeSelector
            currentMode={currentSchoolClosuresMode}
            onModeChange={handleSchoolClosuresModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isSchoolCoolingIndicator && (
          <SchoolCoolingModeSelector
            currentMode={currentSchoolCoolingMode}
            onModeChange={handleSchoolCoolingModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isWaterSupplyIndicator && (
          <WaterSupplyModeSelector
            currentMode={currentWaterSupplyMode}
            onModeChange={handleWaterSupplyModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isWaterTankersIndicator && (
          <WaterTankersModeSelector
            currentMode={currentWaterTankersMode}
            onModeChange={handleWaterTankersModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isHeatDifferentialIndicator && (
          <HeatDifferentialModeSelector
            currentMode={currentHeatDifferentialMode}
            onModeChange={handleHeatDifferentialModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isSurfaceTemperatureIndicator && (
          <SurfaceTemperatureModeSelector
            currentMode={currentSurfaceTemperatureMode}
            onModeChange={handleSurfaceTemperatureModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}
        {isShelterOccupancyIndicator && (
          <ShelterOccupancyModeSelector
            currentMode={currentShelterOccupancyMode}
            onModeChange={handleShelterOccupancyModeChange}
            indicatorName={currentIndicator}
            headerAction={mapViewToggleHeaderAction}
          />
        )}

        <div 
          className={`visualization-container ${
            ((isPowerOutageIndicator && currentPowerOutageMode === 'forecast') || (isGroundwaterIndicator && currentGroundwaterMode === 'forecast') || (isInformalWorkersIndicator && currentInformalWorkersMode === 'forecast') || (isConstructionLaborIndicator && currentConstructionLaborMode === 'forecast'))
              ? (forecastViewMode === 'mapOnly' ? 'full-width-map' : '') 
              : (!showTrendContainer || isChartCollapsed ? 'full-width-map' : '')
          }`}
          ref={visualizationContainerRef}
        >
          {/* Forecast Mode: Show both map and table by default, or map only when map view toggle is ON */}
          {((isPowerOutageIndicator && currentPowerOutageMode === 'forecast') || (isGroundwaterIndicator && currentGroundwaterMode === 'forecast') || (isInformalWorkersIndicator && currentInformalWorkersMode === 'forecast') || (isConstructionLaborIndicator && currentConstructionLaborMode === 'forecast')) ? (
            <>
              {forecastViewMode === 'mapOnly' ? (
                <MapContainer
                  indicatorName={currentIndicator}
                  city={currentCity}
                  isPowerOutage={isPowerOutageIndicator}
                  powerOutageMode={currentPowerOutageMode}
                  isGroundwater={isGroundwaterIndicator}
                  groundwaterMode={currentGroundwaterMode}
                  isInformalWorkers={isInformalWorkersIndicator}
                  informalWorkersMode={currentInformalWorkersMode}
                  isConstructionLabor={isConstructionLaborIndicator}
                  constructionLaborMode={currentConstructionLaborMode}
                  forecastDay={forecastDay}
                  onForecastDayChange={setForecastDay}
                  onForecastDaysAvailable={setForecastDaysAvailable}
                  selectedDate={isPowerOutageIndicator ? startDate : (isGroundwaterIndicator ? startDate : null)}
                  showDateSelector={false}
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  availableMonths={availableMonths}
                />
              ) : (
                <>
                  <MapContainer
                    indicatorName={currentIndicator}
                    city={currentCity}
                    isPowerOutage={isPowerOutageIndicator}
                    powerOutageMode={currentPowerOutageMode}
                    isGroundwater={isGroundwaterIndicator}
                    groundwaterMode={currentGroundwaterMode}
                    isInformalWorkers={isInformalWorkersIndicator}
                    informalWorkersMode={currentInformalWorkersMode}
                    isConstructionLabor={isConstructionLaborIndicator}
                    constructionLaborMode={currentConstructionLaborMode}
                    forecastDay={forecastDay}
                    onForecastDayChange={setForecastDay}
                    onForecastDaysAvailable={setForecastDaysAvailable}
                    selectedDate={isPowerOutageIndicator ? startDate : (isGroundwaterIndicator ? startDate : null)}
                    showDateSelector={false}
                    startDate={startDate}
                    endDate={endDate}
                    onStartDateChange={setStartDate}
                    onEndDateChange={setEndDate}
                    availableMonths={availableMonths}
                  />
                  <ForecastTableContainer
                    city={currentCity}
                    forecastDay={forecastDay}
                    geoJSON={currentGeoJSON}
                    isGroundwater={isGroundwaterIndicator && currentGroundwaterMode === 'forecast'}
                    isInformalWorkers={isInformalWorkersIndicator && currentInformalWorkersMode === 'forecast'}
                    isConstructionLabor={isConstructionLaborIndicator && currentConstructionLaborMode === 'forecast'}
                    indicatorName={currentIndicator}
                  />
                </>
              )}
            </>
          ) : (
            <>
              <MapContainer
                indicatorName={currentIndicator}
                city={currentCity}
                isPowerOutage={isPowerOutageIndicator}
                powerOutageMode={currentPowerOutageMode}
                isGroundwater={isGroundwaterIndicator}
                groundwaterMode={currentGroundwaterMode}
                isInformalWorkers={isInformalWorkersIndicator}
                informalWorkersMode={currentInformalWorkersMode}
                isConstructionLabor={isConstructionLaborIndicator}
                constructionLaborMode={currentConstructionLaborMode}
                isTransportDisruptions={isTransportDisruptionsIndicator}
                transportDisruptionsMode={currentTransportDisruptionsMode}
                isRoadClosures={isRoadClosuresIndicator}
                roadClosuresMode={currentRoadClosuresMode}
                isTrafficSignalFailures={isTrafficSignalFailuresIndicator}
                trafficSignalFailuresMode={currentTrafficSignalFailuresMode}
                isCoolingSystemFailures={isCoolingSystemFailuresIndicator}
                coolingSystemFailuresMode={currentCoolingSystemFailuresMode}
                isSchoolClosures={isSchoolClosuresIndicator}
                schoolClosuresMode={currentSchoolClosuresMode}
                isSchoolCooling={isSchoolCoolingIndicator}
                schoolCoolingMode={currentSchoolCoolingMode}
                isWaterSupply={isWaterSupplyIndicator}
                waterSupplyMode={currentWaterSupplyMode}
                isWaterTankers={isWaterTankersIndicator}
                waterTankersMode={currentWaterTankersMode}
                isHeatDifferential={isHeatDifferentialIndicator}
                heatDifferentialMode={currentHeatDifferentialMode}
                isSurfaceTemperature={isSurfaceTemperatureIndicator}
                surfaceTemperatureMode={currentSurfaceTemperatureMode}
                isShelterOccupancy={isShelterOccupancyIndicator}
                shelterOccupancyMode={currentShelterOccupancyMode}
                forecastDay={forecastDay}
                onForecastDayChange={setForecastDay}
                onForecastDaysAvailable={setForecastDaysAvailable}
                selectedDate={(isPowerOutageIndicator || isGroundwaterIndicator || isTransportDisruptionsIndicator || isRoadClosuresIndicator || isTrafficSignalFailuresIndicator || isCoolingSystemFailuresIndicator || isSchoolClosuresIndicator || isSchoolCoolingIndicator || isWaterSupplyIndicator || isWaterTankersIndicator || isHeatDifferentialIndicator || isSurfaceTemperatureIndicator || isShelterOccupancyIndicator) ? startDate : (isInformalWorkersIndicator || isConstructionLaborIndicator ? startDate : null)}
                showDateSelector={isChartCollapsed}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={setStartDate}
                onEndDateChange={setEndDate}
                availableMonths={availableMonths}
              />
              
              {showTrendContainer && !isChartCollapsed && (
                <TrendContainer
                  indicatorName={currentIndicator}
                  city={currentCity}
                  isPowerOutage={isPowerOutageIndicator}
                  powerOutageMode={currentPowerOutageMode}
                  isGroundwater={isGroundwaterIndicator}
                  groundwaterMode={currentGroundwaterMode}
                  isInformalWorkers={isInformalWorkersIndicator}
                  informalWorkersMode={currentInformalWorkersMode}
                  isConstructionLabor={isConstructionLaborIndicator}
                  constructionLaborMode={currentConstructionLaborMode}
                  isTransportDisruptions={isTransportDisruptionsIndicator}
                  transportDisruptionsMode={currentTransportDisruptionsMode}
                  isRoadClosures={isRoadClosuresIndicator}
                  roadClosuresMode={currentRoadClosuresMode}
                  isTrafficSignalFailures={isTrafficSignalFailuresIndicator}
                  trafficSignalFailuresMode={currentTrafficSignalFailuresMode}
                  isCoolingSystemFailures={isCoolingSystemFailuresIndicator}
                  coolingSystemFailuresMode={currentCoolingSystemFailuresMode}
                  isSchoolClosures={isSchoolClosuresIndicator}
                  schoolClosuresMode={currentSchoolClosuresMode}
                  isSchoolCooling={isSchoolCoolingIndicator}
                  schoolCoolingMode={currentSchoolCoolingMode}
                  isWaterSupply={isWaterSupplyIndicator}
                  waterSupplyMode={currentWaterSupplyMode}
                  isWaterTankers={isWaterTankersIndicator}
                  waterTankersMode={currentWaterTankersMode}
                  isHeatDifferential={isHeatDifferentialIndicator}
                  heatDifferentialMode={currentHeatDifferentialMode}
                  isSurfaceTemperature={isSurfaceTemperatureIndicator}
                  surfaceTemperatureMode={currentSurfaceTemperatureMode}
                  isShelterOccupancy={isShelterOccupancyIndicator}
                  shelterOccupancyMode={currentShelterOccupancyMode}
                  selectedDate={(isPowerOutageIndicator || isGroundwaterIndicator || isInformalWorkersIndicator || isConstructionLaborIndicator || isTransportDisruptionsIndicator || isRoadClosuresIndicator || isTrafficSignalFailuresIndicator || isCoolingSystemFailuresIndicator || isSchoolClosuresIndicator || isSchoolCoolingIndicator || isWaterSupplyIndicator || isWaterTankersIndicator || isHeatDifferentialIndicator || isSurfaceTemperatureIndicator || isShelterOccupancyIndicator) ? startDate : null}
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  availableMonths={availableMonths}
                />
              )}
            </>
          )}
        </div>

        {currentIndicator && (
          <RecommendedMeasures
            indicatorName={currentIndicator}
            sector={currentSector}
          />
        )}
      </div>
    </section>
  );
};

export default DetailView;

